/**
 * SocialHistoryPanel — Phase 7
 *
 * Categorized social history panel with per-category structured cards.
 * One entry per category, upserted not versioned.
 * Tobacco, Alcohol, Substance, Housing, Diet, Exercise, Occupation, Other.
 */
import React, { useState, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Plus, Edit3, X, Check, RefreshCw, Users, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  tobacco:        { label: 'Tobacco',         color: 'bg-slate-100 text-slate-700 border-slate-200', fields: ['status', 'amount_per_day', 'pack_years', 'quit_date'] },
  alcohol:        { label: 'Alcohol',          color: 'bg-amber-50 text-amber-700 border-amber-200',  fields: ['frequency', 'drinks_per_occasion', 'audit_c_score'] },
  substance:      { label: 'Substance Use',    color: 'bg-orange-50 text-orange-700 border-orange-200', fields: ['type', 'status', 'years_of_use'] },
  housing:        { label: 'Housing / SDOH',   color: 'bg-blue-50 text-blue-700 border-blue-200',    fields: ['housing_status', 'food_security'] },
  diet:           { label: 'Diet',             color: 'bg-emerald-50 text-emerald-700 border-emerald-200', fields: ['description', 'restrictions'] },
  exercise:       { label: 'Exercise',         color: 'bg-teal-50 text-teal-700 border-teal-200',    fields: ['frequency', 'type'] },
  occupation:     { label: 'Occupation',       color: 'bg-violet-50 text-violet-700 border-violet-200', fields: ['job_title', 'industry', 'exposures'] },
  social_support: { label: 'Social Support',   color: 'bg-pink-50 text-pink-700 border-pink-200',    fields: ['lives_with', 'support_level'] },
  sexual_history: { label: 'Sexual History',   color: 'bg-gray-50 text-gray-600 border-gray-200',    fields: ['status', 'partners', 'contraception'] },
  other:          { label: 'Other',            color: 'bg-gray-50 text-gray-600 border-gray-200',    fields: [] },
};

const DISPLAY_ORDER = [
  'tobacco', 'alcohol', 'substance', 'housing', 'diet',
  'exercise', 'occupation', 'social_support', 'sexual_history', 'other',
];

function SummarizeSH({ category, entry }) {
  const sd = entry?.structured_data || {};
  if (category === 'tobacco') {
    const status = sd.status || 'unknown';
    if (status === 'current') return <span className="text-sm">Current smoker{sd.pack_years ? ` · ${sd.pack_years} pk-yr` : ''}</span>;
    if (status === 'former') return <span className="text-sm text-muted-foreground">Former smoker{sd.quit_date ? ` · quit ${sd.quit_date}` : ''}</span>;
    if (status === 'never')  return <span className="text-sm text-muted-foreground">Never smoked</span>;
  }
  if (category === 'alcohol') {
    const freq = sd.frequency || '';
    if (freq) return <span className="text-sm">{freq}{sd.drinks_per_occasion ? `, ${sd.drinks_per_occasion} drinks/occasion` : ''}</span>;
  }
  if (category === 'housing') {
    const hs = sd.housing_status || '';
    if (hs) return <span className="text-sm capitalize">{hs}</span>;
  }
  if (category === 'exercise') {
    const freq = sd.frequency || '';
    if (freq) return <span className="text-sm">{freq}</span>;
  }
  if (entry?.free_text) return <span className="text-sm">{entry.free_text}</span>;
  return <span className="text-sm text-muted-foreground italic">Documented</span>;
}

function CategoryCard({ category, entry, patientId, onUpdated }) {
  const cfg = CATEGORY_CONFIG[category];
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sd, setSd] = useState(entry?.structured_data || {});
  const [freeText, setFreeText] = useState(entry?.free_text || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/social-history`, { category, structured_data: sd, free_text: freeText });
      toast.success(`${cfg.label} updated`);
      setEditing(false);
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${cfg.label} from social history?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/patients/${patientId}/social-history/${category}`);
      toast.success(`${cfg.label} removed`);
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className={`rounded-xl border p-3 ${!entry ? 'border-dashed border-muted' : 'border-border bg-card'}`}
      data-testid={`sh-card-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Badge variant="outline" className={`text-[9px] shrink-0 ${cfg.color}`}>{cfg.label}</Badge>
          {entry && !editing && <SummarizeSH category={category} entry={entry} />}
          {!entry && <span className="text-xs text-muted-foreground italic">Not recorded</span>}
          {entry?.icd10_code && (
            <Badge variant="outline" className="text-[8px] font-mono shrink-0 ml-auto">{entry.icd10_code}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {entry && (
            <button onClick={() => setExpanded(e => !e)} className="text-muted-foreground hover:text-foreground p-0.5">
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          <button onClick={() => { setEditing(e => !e); setExpanded(true); }}
            className="text-muted-foreground hover:text-foreground p-0.5" data-testid={`sh-edit-${category}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          {entry && (
            <button onClick={handleDelete} disabled={deleting}
              className="text-muted-foreground hover:text-red-500 p-0.5" data-testid={`sh-delete-${category}`}>
              {deleting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {(expanded || editing) && (
        <div className="mt-2 space-y-2 border-t pt-2">
          {editing ? (
            <>
              <StructuredDataEditor category={category} fields={cfg.fields} value={sd} onChange={setSd} />
              <div>
                <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Free text notes</label>
                <textarea value={freeText} onChange={e => setFreeText(e.target.value)} rows={2}
                  className="w-full mt-0.5 text-xs border rounded px-2 py-1.5 bg-background resize-none" />
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" className="h-7 text-[10px] gap-1" onClick={handleSave} disabled={saving}
                  data-testid={`sh-save-${category}`}>
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Save
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                  onClick={() => { setEditing(false); setSd(entry?.structured_data || {}); setFreeText(entry?.free_text || ''); }}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              {Object.entries(entry?.structured_data || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs text-muted-foreground">
                  <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="font-medium text-foreground">{String(v)}</span>
                </div>
              ))}
              {entry?.free_text && <p className="text-xs text-muted-foreground italic">{entry.free_text}</p>}
              {(entry?.lifecycle_history || []).length > 1 && (
                <p className="text-[9px] text-muted-foreground">
                  Updated {new Date(entry.updated_at || entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StructuredDataEditor({ category, fields, value, onChange }) {
  if (category === 'tobacco') return (
    <div className="space-y-1.5">
      <div>
        <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Status</label>
        <div className="flex gap-1 mt-0.5">
          {['never', 'former', 'current'].map(s => (
            <button key={s} onClick={() => onChange({ ...value, status: s })}
              className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${value.status === s ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      {value.status !== 'never' && (
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <label className="text-[9px] text-muted-foreground">Pack-years</label>
            <input type="number" value={value.pack_years || ''} onChange={e => onChange({...value, pack_years: e.target.value ? parseFloat(e.target.value) : null})}
              className="w-full text-[10px] border rounded px-1.5 py-1 bg-background" min="0" />
          </div>
          {value.status === 'former' && (
            <div>
              <label className="text-[9px] text-muted-foreground">Quit date</label>
              <input type="date" value={value.quit_date || ''} onChange={e => onChange({...value, quit_date: e.target.value})}
                className="w-full text-[10px] border rounded px-1.5 py-1 bg-background" />
            </div>
          )}
        </div>
      )}
    </div>
  );
  if (category === 'alcohol') return (
    <div className="space-y-1.5">
      <div>
        <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Frequency</label>
        <input type="text" placeholder="e.g. 1-2x/week, daily, rarely" value={value.frequency || ''}
          onChange={e => onChange({...value, frequency: e.target.value})}
          className="w-full mt-0.5 text-xs border rounded px-2 py-1 bg-background" />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[9px] text-muted-foreground">Drinks/occasion</label>
          <input type="number" value={value.drinks_per_occasion || ''} onChange={e => onChange({...value, drinks_per_occasion: e.target.value ? parseInt(e.target.value) : null})}
            className="w-full text-[10px] border rounded px-1.5 py-1 bg-background" min="0" />
        </div>
        <div>
          <label className="text-[9px] text-muted-foreground">AUDIT-C score</label>
          <input type="number" value={value.audit_c_score ?? ''} onChange={e => onChange({...value, audit_c_score: e.target.value !== '' ? parseInt(e.target.value) : null, audit_c_positive: parseInt(e.target.value) >= 3})}
            className="w-full text-[10px] border rounded px-1.5 py-1 bg-background" min="0" max="12" />
        </div>
      </div>
    </div>
  );
  if (category === 'housing') return (
    <div>
      <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Housing Status</label>
      <div className="flex gap-1 mt-0.5 flex-wrap">
        {['stable', 'unstable', 'homeless', 'temporary'].map(s => (
          <button key={s} onClick={() => onChange({...value, housing_status: s})}
            className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors capitalize ${value.housing_status === s ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
  if (category === 'exercise') return (
    <div>
      <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Frequency</label>
      <div className="flex gap-1 mt-0.5 flex-wrap">
        {['sedentary', '1-2x/week', '3-4x/week', '5+x/week'].map(s => (
          <button key={s} onClick={() => onChange({...value, frequency: s})}
            className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${value.frequency === s ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
  // Generic text fields for other categories
  return (
    <div className="space-y-1.5">
      {fields.map(field => (
        <div key={field}>
          <label className="text-[9px] text-muted-foreground capitalize">{field.replace(/_/g, ' ')}</label>
          <input type="text" value={value[field] || ''} onChange={e => onChange({...value, [field]: e.target.value})}
            className="w-full mt-0.5 text-xs border rounded px-2 py-1 bg-background" />
        </div>
      ))}
    </div>
  );
}

export function SocialHistoryPanel({ patientId, initialHistory }) {
  const [history, setHistory] = useState(initialHistory || []);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}/social-history`);
      setHistory(res.data.social_history || []);
    } catch {}
    finally { setLoading(false); }
  }, [patientId]);

  const historyMap = Object.fromEntries(history.map(e => [e.category, e]));

  return (
    <div data-testid="social-history-panel">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Social History
            <Badge variant="outline" className="text-[10px] ml-auto">{history.length} recorded</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-2">
          {loading ? <p className="text-xs text-muted-foreground">Loading...</p> : (
            DISPLAY_ORDER.map(cat => (
              <CategoryCard
                key={cat}
                category={cat}
                entry={historyMap[cat]}
                patientId={patientId}
                onUpdated={refresh}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
